import { useEffect, useState } from "react";
import styled from "styled-components";
import {
    AiOutlineFilePdf,
    AiOutlineFileImage,
    AiOutlineFileWord,
    AiOutlineFileExcel,
    AiOutlineFileUnknown,
} from "react-icons/ai";

import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import ViewContainer from "../../../Componentes/Utils/ViewContainer";
import SmallLoading from "../../../Componentes/Utils/SmallLoading";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import { formatarData } from "../../../Funcoes/FormatarData";
import useApi from "../../../hooks/useApi";
import { useToast } from "../../../contexts/ToastContext";

const Titulo = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
`;

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
`;

const ContainerAnexos = styled.div`
    width: 100%;

    > p {
        font-size: 14px;
        font-weight: 500;
        color: #555;
        margin: 0 0 5px 5px;
    }
`;

const ContainerAnexo = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    padding: 10px;
    box-sizing: border-box;
    background-color: #f6f8fa;
    border: 1px solid #ccc;
    margin-bottom: 5px;
    border-radius: 10px;
`;

const AnexoIconContainer = styled.div`
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    margin-right: 10px;
    flex-shrink: 0;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.15);
`;

const AnexoNome = styled.a`
    font-size: 14px;
    color: #2563eb;
    margin: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const SemAnexos = styled.p`
    margin: 0 0 0 5px;
    font-size: 13px;
    color: #6b7280;
`;

const ErrorMsg = styled.p`
    margin: 0;
    font-size: 13px;
    color: #b91c1c;
    text-align: center;
`;

function formatarDataHora(value) {
    if (!value) return "-";
    try {
        return formatarData(value);
    } catch {
        return String(value);
    }
}

function formatarTempo(segundos) {
    if (segundos == null || segundos === "") return "-";
    const total = Number(segundos);
    if (!Number.isFinite(total)) return "-";
    const horas = Math.floor(total / 3600);
    const minutos = Math.floor((total % 3600) / 60);
    const seg = total % 60;
    return [horas, minutos, seg].map((n) => String(n).padStart(2, "0")).join(":");
}

function formatarCpf(value) {
    if (!value) return "-";
    const digits = String(value).replace(/\D/g, "");
    return digits.length === 11 ? formatCPF(digits) : value;
}

function formatarMoeda(value) {
    if (value == null || value === "") return "-";
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function displayValue(value) {
    if (value == null || value === "") return "-";
    return String(value);
}

function getFileIcon(filename) {
    const name = String(filename ?? "").toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(name)) {
        return <AiOutlineFileImage size={22} color="#4a90e2" />;
    }
    if (name.endsWith(".pdf")) return <AiOutlineFilePdf size={22} color="#e63946" />;
    if (/\.(doc|docx)$/i.test(name)) return <AiOutlineFileWord size={22} color="#2a6db0" />;
    if (/\.(xls|xlsx)$/i.test(name)) return <AiOutlineFileExcel size={22} color="#207245" />;
    return <AiOutlineFileUnknown size={22} color="#555" />;
}

function nomeArquivo(caminho) {
    if (!caminho) return "Arquivo";
    const parts = String(caminho).split("/");
    return parts[parts.length - 1] || "Arquivo";
}

const ModalViewAtendimento = ({ atendimentoId, fechar }) => {
    const api = useApi();
    const { notifyError } = useToast();

    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [atendimento, setAtendimento] = useState(null);

    const carregarAtendimento = async (id) => {
        setLoading(true);
        setErro(null);
        setAtendimento(null);

        try {
            const { data } = await api.get("/Painel/API/AtendimentoAmb/Get/index.php", {
                params: { id },
            });

            if (!data?.success || !data?.data) {
                const msg = data?.message || "Atendimento não encontrado.";
                setErro(msg);
                notifyError(msg);
                return;
            }

            setAtendimento(data.data);
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.message || "Erro ao carregar atendimento.";
            setErro(msg);
            notifyError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!atendimentoId) return;
        carregarAtendimento(atendimentoId);
        // useApi() retorna nova instância a cada render — mesma regra da listagem em Content.jsx
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [atendimentoId]);

    const anexos = Array.isArray(atendimento?.anexos) ? atendimento.anexos : [];
    const tipoAtendimento = atendimento?.atendimento;
    const showInternacao = tipoAtendimento === "EXTERNO";
    const showStatusInternacao =
        showInternacao && atendimento?.internacao === "SIM";

    return (
        <Overlay state>
            <Modal header close={fechar} size="md">
                <Titulo>VISUALIZAR ATENDIMENTO</Titulo>

                {loading && <SmallLoading text="Carregando atendimento…" />}

                {!loading && erro && <ErrorMsg>{erro}</ErrorMsg>}

                {!loading && !erro && atendimento && (
                    <Container>
                        <ViewContainer label="ID" valor={atendimento.id} />
                        <ViewContainer label="CPF" valor={formatarCpf(atendimento.cpf)} />
                        <ViewContainer label="NOME" valor={displayValue(atendimento.nome)} />
                        <ViewContainer label="MATRÍCULA" valor={displayValue(atendimento.matricula)} />
                        <ViewContainer label="FUNÇÃO" valor={displayValue(atendimento.funcao)} />
                        <ViewContainer
                            label="CENTRO DE CUSTO"
                            valor={displayValue(atendimento.centro_custo)}
                        />
                        <ViewContainer label="CONTRATO" valor={displayValue(atendimento.contrato)} />
                        <ViewContainer label="LÍDER" valor={displayValue(atendimento.lider)} />
                        <ViewContainer label="QUEIXA / OCORRÊNCIA" valor={displayValue(atendimento.queixa)} />
                        <ViewContainer label="TIPO ATENDIMENTO" valor={displayValue(tipoAtendimento)} />
                        {showInternacao && (
                            <ViewContainer
                                label="INTERNAÇÃO"
                                valor={displayValue(atendimento.internacao)}
                            />
                        )}
                        {showStatusInternacao && (
                            <ViewContainer
                                label="STATUS DA INTERNAÇÃO"
                                valor={displayValue(atendimento.obito_alta)}
                            />
                        )}
                        <ViewContainer label="DESCRIÇÃO" valor={displayValue(atendimento.descricao)} />
                        <ViewContainer label="SOLUÇÃO" valor={displayValue(atendimento.solucao)} />
                        <ViewContainer label="CUSTOS" valor={formatarMoeda(atendimento.custo)} />
                        <ViewContainer label="STATUS" valor={displayValue(atendimento.status)} />
                        <ViewContainer
                            label="DATA INÍCIO"
                            valor={formatarDataHora(atendimento.data_inicio)}
                        />
                        <ViewContainer
                            label="DATA FINAL"
                            valor={formatarDataHora(atendimento.data_final)}
                        />
                        <ViewContainer
                            label="TEMPO"
                            valor={formatarTempo(atendimento.segundos)}
                        />
                        <ViewContainer
                            label="PREENCHIDO POR"
                            valor={displayValue(atendimento.preenchido)}
                        />

                        <ContainerAnexos>
                            <p>ANEXOS</p>
                            {anexos.length === 0 ? (
                                <SemAnexos>Nenhum anexo.</SemAnexos>
                            ) : (
                                anexos.map((anexo) => {
                                    const nome = nomeArquivo(anexo.caminho);
                                    return (
                                        <ContainerAnexo key={anexo.id ?? anexo.caminho}>
                                            <AnexoIconContainer>
                                                {getFileIcon(nome)}
                                            </AnexoIconContainer>
                                            <AnexoNome
                                                href={anexo.caminho}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {nome}
                                            </AnexoNome>
                                        </ContainerAnexo>
                                    );
                                })
                            )}
                        </ContainerAnexos>
                    </Container>
                )}
            </Modal>
        </Overlay>
    );
};

export default ModalViewAtendimento;
